import { TemplateAssetIndexEntry, TemplateDto } from "@exibra/shared";
import { ApiService } from "./api/api.service";

class TemplateService extends ApiService {
    private readonly API_URL = "/templates";

    async getCustomTemplates(tenantId: number): Promise<TemplateDto[]> {
        const response = await this.axios.get(`${this.API_URL}`, {
            params: {
                tenantId,
            },
        });
        return response.data;
    }

    async saveTemplate(template: TemplateDto, isCreate: boolean): Promise<TemplateDto> {
        if (isCreate) {
            // Create new template
            const response = await this.axios.post(`${this.API_URL}/`, template);
            return response.data;
        } else {
            // Update existing template
            const response = await this.axios.put(`${this.API_URL}/${template.id}`, template);
            return response.data;
        }
    }

    async deleteTemplate(templateId: number): Promise<void> {
        await this.axios.delete(`${this.API_URL}/custom/${templateId}`);
    }

    /**
     * Legacy v1 image upload — returns just the URL. Kept for the v1 editor.
     */
    async uploadImage(file: File): Promise<string> {
        const formData = new FormData();
        formData.append("image", file);

        const response = await this.axios.post(`${this.API_URL}/upload`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        return response.data.url;
    }

    /**
     * Content-addressed asset upload for v2 templates. Returns the full asset descriptor
     * including `cacheKey` so it can be embedded into VideoItem / ImageItem layers.
     */
    async uploadAsset(
        file: File,
        tenantId: number,
        onProgress?: (loaded: number, total: number) => void,
    ): Promise<TemplateAssetIndexEntry> {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("tenantId", String(tenantId));

        const response = await this.axios.post(`${this.API_URL}/assets/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (event) => {
                if (onProgress && event.total) {
                    onProgress(event.loaded, event.total);
                }
            },
        });

        return response.data;
    }

    async getTemplateById(templateId: number): Promise<TemplateDto> {
        const response = await this.axios.get(`${this.API_URL}/${templateId}`);
        return response.data;
    }

    async duplicateTemplate(templateId: number): Promise<TemplateDto> {
        const response = await this.axios.post(`${this.API_URL}/duplicate/${templateId}`);
        return response.data;
    }

    /** Creates a v2 copy of an existing v1 template. Source is left untouched. */
    async migrateToV2(templateId: number): Promise<TemplateDto> {
        const response = await this.axios.post(`${this.API_URL}/migrate-to-v2/${templateId}`);
        return response.data;
    }

    /** Polls processing status for uploaded assets (videos transcode in the background). */
    async getAssetStatuses(
        tenantId: number,
        cacheKeys: string[],
    ): Promise<TemplateAssetIndexEntry[]> {
        if (cacheKeys.length === 0) return [];
        const response = await this.axios.get(`${this.API_URL}/assets/status`, {
            params: { tenantId, keys: cacheKeys.join(",") },
        });
        return response.data;
    }
}

export const templateService = new TemplateService();
